import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { auth as authApi, clients as clientsApi } from '@/utils/api';
import { User, Mail, Building2, Lock, Eye, EyeOff, Save, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientProfile() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mustReset = !!user?.must_reset_password;

  useEffect(() => {
    clientsApi.list().then(r => setClient(r.data[0] || null)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) return setError('New password must be at least 8 characters.');
    if (next !== confirm) return setError('New password and confirmation do not match.');
    if (next === current) return setError('New password must be different from the current password.');
    setSaving(true);
    try {
      await authApi.changePassword(current, next);
      toast.success('Password changed successfully');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  const strength = (() => {
    if (!next) return { score: 0, label: '', color: 'var(--border)' };
    let s = 0;
    if (next.length >= 8) s++;
    if (next.length >= 12) s++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) s++;
    if (/\d/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    const levels = [
      { score: 1, label: 'Weak', color: '#B34A3A' },
      { score: 2, label: 'Fair', color: '#D9A441' },
      { score: 3, label: 'Good', color: '#D9A441' },
      { score: 4, label: 'Strong', color: '#3C8A5A' },
      { score: 5, label: 'Excellent', color: '#3C8A5A' },
    ];
    return levels[Math.max(0, s - 1)] || levels[0];
  })();

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 animate-fade-in" data-testid="client-profile-page">
      <h1 className="text-3xl font-black mb-1" style={{ color: 'var(--ink)' }}>Your profile</h1>
      <p className="text-sm mb-8" style={{ color: 'var(--muted)' }}>Account details and password.</p>

      {mustReset && (
        <div className="card-base p-4 mb-6 flex gap-3 items-start" style={{ background: '#D9A44115', borderColor: '#D9A441' }} data-testid="must-reset-banner">
          <AlertCircle size={16} style={{ color: '#D9A441', marginTop: 2 }} />
          <div>
            <p className="text-sm font-bold" style={{ color: '#7A5A00' }}>Please change your password</p>
            <p className="text-xs mt-0.5" style={{ color: '#7A5A00' }}>You're using a temporary password issued by your operator. Change it below to secure your account.</p>
          </div>
        </div>
      )}

      {/* Account details — read-only */}
      <div className="card-base p-5 mb-6" data-testid="profile-account-card">
        <h2 className="text-sm font-bold uppercase tracking-wide mb-4" style={{ color: 'var(--muted)' }}>Account details</h2>
        <div className="space-y-3">
          <InfoRow icon={User} label="Name" value={user?.name} />
          <InfoRow icon={Mail} label="Email" value={user?.email} />
          {client?.company_name && <InfoRow icon={Building2} label="Company" value={client.company_name} />}
        </div>
        <p className="text-xs mt-4 pt-3" style={{ color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
          Need to change your name, email, or company? Contact your operator and they'll update it for you.
        </p>
      </div>

      {/* Change password */}
      <form onSubmit={submit} className="card-base p-5" data-testid="change-password-form">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={14} style={{ color: '#006080' }} />
          <h2 className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--ink)' }}>Change password</h2>
        </div>

        <div className="space-y-3">
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={show.current}
            onToggle={() => setShow(s => ({ ...s, current: !s.current }))}
            testId="current-password-input"
            autoComplete="current-password"
          />
          <div>
            <PasswordField
              label="New password"
              value={next}
              onChange={setNext}
              show={show.next}
              onToggle={() => setShow(s => ({ ...s, next: !s.next }))}
              testId="new-password-input"
              autoComplete="new-password"
            />
            {next && (
              <div className="mt-2 flex items-center gap-2" data-testid="password-strength">
                <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                  <div className="h-full transition-all" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                </div>
                <span className="text-xs font-semibold" style={{ color: strength.color }}>{strength.label}</span>
              </div>
            )}
          </div>
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={show.confirm}
            onToggle={() => setShow(s => ({ ...s, confirm: !s.confirm }))}
            testId="confirm-password-input"
            autoComplete="new-password"
          />
        </div>

        {error && (
          <div className="p-3 rounded-btn text-sm mt-3" style={{ background: 'rgba(179,74,58,0.1)', color: '#B34A3A', border: '1px solid rgba(179,74,58,0.2)' }} data-testid="change-password-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !current || !next || !confirm}
          className="btn-primary flex items-center justify-center gap-2 py-2.5 px-4 mt-4 text-sm"
          data-testid="change-password-submit"
        >
          <Save size={14} /> {saving ? 'Saving…' : 'Change password'}
        </button>
        <p className="text-xs mt-3" style={{ color: 'var(--muted)' }}>
          Your password must be at least 8 characters and different from the current one.
        </p>
      </form>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: '#E6F1F4' }}>
        <Icon size={14} style={{ color: '#006080' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{label}</p>
        <p className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{value || '—'}</p>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testId, autoComplete }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>{label}</label>
      <div className="relative">
        <input
          className="input-agency pr-10"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required
          data-testid={testId}
        />
        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={onToggle} tabIndex={-1}>
          {show ? <EyeOff size={16} color="var(--muted)" /> : <Eye size={16} color="var(--muted)" />}
        </button>
      </div>
    </div>
  );
}
